import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RestaurantStaffManager from "@/components/restaurant/RestaurantStaffManager";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { PageHeader, KpiCard, Panel, EmptyState, formatEur } from "@/components/shared/PageUi";
import { toast } from "sonner";
import {
  Utensils, GlassWater, Cake, Soup, ShoppingBag, Plus, Pencil, Trash2,
  QrCode, Download, ExternalLink, ChefHat, CheckCircle2, Clock, Banknote, Receipt,
  Users, Wallet, ListChecks, RefreshCw, Coffee, Pizza,
} from "lucide-react";

const ORDER_STATUSES = {
  received:   { label: "Uusi tilaus",            short: "Uusi",          cls: "bg-blue-500/15 text-blue-300 border-blue-500/30",         dot: "bg-blue-400",    next: "preparing", nextLabel: "Aloita valmistus",   nextIcon: ChefHat },
  preparing:  { label: "Valmistuksessa",         short: "Keittiössä",    cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",      dot: "bg-amber-400",   next: "ready",     nextLabel: "Merkitse valmiiksi", nextIcon: CheckCircle2 },
  ready:      { label: "Valmis tarjoiltavaksi",  short: "Valmis",        cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",dot: "bg-emerald-400", next: "served",    nextLabel: "Vie pöytään",        nextIcon: Utensils },
  served:     { label: "Tarjoiltu pöytään",      short: "Pöydässä",      cls: "bg-purple-500/15 text-purple-300 border-purple-500/30",   dot: "bg-purple-400",  next: "paid",      nextLabel: "Maksu vastaanotettu",nextIcon: Banknote },
  paid:       { label: "Maksu vastaanotettu",    short: "Maksettu",      cls: "bg-green-500/15 text-green-300 border-green-500/30",      dot: "bg-green-400",   next: null,        nextLabel: null,                 nextIcon: null },
  cancelled:  { label: "Peruutettu",             short: "Peruutettu",    cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",         dot: "bg-zinc-400",    next: null,        nextLabel: null,                 nextIcon: null },
};

const ORDER_FLOW = ["received", "preparing", "ready", "served", "paid"];

const SOURCE_LABEL = {
  customer_qr: { label: "Asiakas (QR)", color: "text-blue-300" },
  counter:     { label: "Tiski",        color: "text-amber-300" },
  staff:       { label: "Henkilökunta", color: "text-emerald-300" },
};

const ICON_MAP = { utensils: Utensils, "glass-water": GlassWater, cake: Cake, soup: Soup, coffee: Coffee, pizza: Pizza };

const defaultCategory = () => ({ name: "", description: "", icon: "utensils", color: "#a78bfa", sort_order: 0, is_active: true });
const defaultProduct = () => ({ category_id: "", name: "", description: "", price: 0, image_url: "", allergens: [], vat_percent: 14, is_available: true, sort_order: 0 });
const defaultTable = () => ({ table_number: "", capacity: 4, location: "Sali", notes: "", is_active: true });

export default function RestaurantDashboard() {
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [catDialog, setCatDialog] = useState({ open: false, mode: "create", data: defaultCategory() });
  const [prodDialog, setProdDialog] = useState({ open: false, mode: "create", data: defaultProduct() });
  const [tableDialog, setTableDialog] = useState({ open: false, mode: "create", data: defaultTable() });
  const [qrPreview, setQrPreview] = useState(null);    // selected table for QR display
  const [orderDialog, setOrderDialog] = useState({ open: false, data: { table_id: "", customer_name: "", items: [], comment: "" } });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c, p, t, o, ao] = await Promise.all([
        api.get("/restaurant/stats"),
        api.get("/restaurant/categories"),
        api.get("/restaurant/products"),
        api.get("/restaurant/tables"),
        api.get("/restaurant/orders?limit=200"),
        api.get("/restaurant/orders/active"),
      ]);
      setStats(s.data);
      setCategories(c.data || []);
      setProducts(p.data || []);
      setTables(t.data || []);
      setOrders(o.data || []);
      setActiveOrders(ao.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh active orders every 8s
  useEffect(() => {
    const i = setInterval(async () => {
      try { const r = await api.get("/restaurant/orders/active"); setActiveOrders(r.data || []); } catch (_e) { /* ignore */ }
    }, 8000);
    return () => clearInterval(i);
  }, []);

  // -- Categories
  const saveCategory = async () => {
    const d = catDialog.data;
    if (!d.name?.trim()) return toast.error("Osion nimi on pakollinen");
    setSaving(true);
    try {
      if (catDialog.mode === "create") await api.post("/restaurant/categories", d);
      else await api.put(`/restaurant/categories/${d.id}`, d);
      toast.success("Tallennettu");
      setCatDialog({ open: false, mode: "create", data: defaultCategory() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const delCategory = async (c) => {
    if (!window.confirm(`Poista menu-osio "${c.name}"?`)) return;
    try { await api.delete(`/restaurant/categories/${c.id}`); toast.success("Poistettu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // -- Products
  const saveProduct = async () => {
    const d = prodDialog.data;
    if (!d.name?.trim()) return toast.error("Tuotteen nimi on pakollinen");
    if (!d.category_id) return toast.error("Valitse menu-osio");
    setSaving(true);
    try {
      if (prodDialog.mode === "create") await api.post("/restaurant/products", d);
      else await api.put(`/restaurant/products/${d.id}`, d);
      toast.success("Tallennettu");
      setProdDialog({ open: false, mode: "create", data: defaultProduct() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const delProduct = async (p) => {
    if (!window.confirm(`Poista tuote "${p.name}"?`)) return;
    try { await api.delete(`/restaurant/products/${p.id}`); toast.success("Poistettu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // -- Tables
  const saveTable = async () => {
    const d = tableDialog.data;
    if (!d.table_number?.toString().trim()) return toast.error("Pöytänumero on pakollinen");
    setSaving(true);
    try {
      if (tableDialog.mode === "create") await api.post("/restaurant/tables", d);
      else await api.put(`/restaurant/tables/${d.id}`, d);
      toast.success("Tallennettu");
      setTableDialog({ open: false, mode: "create", data: defaultTable() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const delTable = async (t) => {
    if (!window.confirm(`Poista pöytä ${t.table_number}?`)) return;
    try { await api.delete(`/restaurant/tables/${t.id}`); toast.success("Poistettu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const regenQr = async (t) => {
    if (!window.confirm("Generoi uusi QR-koodi? Vanha lakkaa toimimasta.")) return;
    try { await api.post(`/restaurant/tables/${t.id}/regenerate-qr`); toast.success("Uusi QR luotu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // -- Orders
  const updateStatus = async (order, status) => {
    try {
      await api.put(`/restaurant/orders/${order.id}/status`, { status });
      toast.success(`Tila päivitetty: ${ORDER_STATUSES[status]?.label || status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelOrder = async (order) => {
    if (!window.confirm(`Peruuta tilaus ${order.order_number}?`)) return;
    try { await api.delete(`/restaurant/orders/${order.id}`); toast.success("Peruutettu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // -- Direct order from staff
  const openStaffOrder = (tableId = "") => setOrderDialog({ open: true, data: { table_id: tableId, customer_name: "", items: [], comment: "" } });
  const addItem = (product) => {
    const items = [...orderDialog.data.items];
    const existing = items.findIndex(i => i.product_id === product.id);
    if (existing >= 0) items[existing].quantity += 1;
    else items.push({ product_id: product.id, name: product.name, price: product.price, quantity: 1, vat_percent: product.vat_percent || 14, notes: "" });
    setOrderDialog({ ...orderDialog, data: { ...orderDialog.data, items } });
  };
  const removeItem = (idx) => {
    const items = orderDialog.data.items.filter((_, i) => i !== idx);
    setOrderDialog({ ...orderDialog, data: { ...orderDialog.data, items } });
  };
  const orderTotal = useMemo(() =>
    orderDialog.data.items.reduce((a, it) => a + it.quantity * it.price, 0),
    [orderDialog.data.items]
  );
  const submitStaffOrder = async () => {
    const d = orderDialog.data;
    if (d.items.length === 0) return toast.error("Lisää vähintään yksi tuote");
    const tbl = tables.find(t => t.id === d.table_id);
    setSaving(true);
    try {
      await api.post("/restaurant/orders", {
        ...d,
        table_number: tbl?.table_number,
        source: "staff",
      });
      toast.success("Tilaus lähetetty keittiöön");
      setOrderDialog({ open: false, data: { table_id: "", customer_name: "", items: [], comment: "" } });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  // QR URL builder
  const qrUrl = (table) => `${window.location.origin}/r/${table.qr_token}`;
  const downloadQr = (table) => {
    // Open QR API URL (uses public qrserver.com — works without backend)
    const url = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=10&data=${encodeURIComponent(qrUrl(table))}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr_poyta_${table.table_number}.png`;
    a.target = "_blank";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const catName = (id) => categories.find(c => c.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="restaurant-dashboard">
      <PageHeader
        kicker="Ravintolan POS"
        title="Ravintola"
        description="Menut, pöydät, QR-tilaukset ja henkilökunnan tilausnäkymä – kaikki yhdessä."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => openStaffOrder("")} className="btn-luxe inline-flex items-center gap-2" data-testid="staff-new-order">
              <Plus className="w-4 h-4" /> Uusi tilaus
            </Button>
            <Link to="/restaurant/waiter">
              <Button variant="outline" className="border-cyan-400/30 inline-flex items-center gap-2 text-cyan-200" data-testid="open-waiter">
                <Users className="w-4 h-4" /> Tarjoilija
              </Button>
            </Link>
            <Link to="/restaurant/kitchen">
              <Button variant="outline" className="border-amber-400/30 inline-flex items-center gap-2 text-amber-200" data-testid="open-kitchen">
                <ChefHat className="w-4 h-4" /> Kokki
              </Button>
            </Link>
            <Link to="/restaurant/cashier">
              <Button variant="outline" className="border-emerald-400/30 inline-flex items-center gap-2 text-emerald-200" data-testid="open-cashier">
                <Receipt className="w-4 h-4" /> Kassa
              </Button>
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard icon={<Wallet className="w-4 h-4" />} label="Tämän päivän liikevaihto" value={formatEur(stats?.today_revenue || 0)} accent="text-emerald-300" testid="kpi-today-revenue" loading={loading} />
        <KpiCard icon={<ListChecks className="w-4 h-4" />} label="Tämän päivän tilaukset" value={stats?.today_orders ?? "—"} testid="kpi-today-orders" loading={loading} />
        <KpiCard icon={<Clock className="w-4 h-4" />} label="Aktiiviset tilaukset" value={stats?.active_orders ?? "—"} accent="text-amber-300" testid="kpi-active" loading={loading} />
        <KpiCard icon={<Users className="w-4 h-4" />} label="Pöydät" value={stats?.tables_count ?? "—"} testid="kpi-tables" loading={loading} />
        <KpiCard icon={<Utensils className="w-4 h-4" />} label="Tuotteita" value={stats?.products_count ?? "—"} testid="kpi-products" loading={loading} />
      </div>

      <Tabs defaultValue="active" className="space-y-4">
        <TabsList className="bg-white/[0.03] border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="active" data-testid="tab-active">Aktiiviset tilaukset ({activeOrders.length})</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Menu-osiot</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Tuotteet</TabsTrigger>
          <TabsTrigger value="tables" data-testid="tab-tables">Pöydät + QR-koodit</TabsTrigger>
          <TabsTrigger value="all-orders" data-testid="tab-all-orders">Kaikki tilaukset</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">Henkilökunta</TabsTrigger>
        </TabsList>

        {/* ACTIVE ORDERS – kioski-tyylinen näkymä */}
        <TabsContent value="active">
          {activeOrders.length === 0 ? (
            <EmptyState icon={<ChefHat className="w-8 h-8" />} message="Ei aktiivisia tilauksia juuri nyt" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="active-orders-grid">
              {activeOrders.map(o => <OrderCard key={o.id} order={o} onUpdate={updateStatus} onCancel={cancelOrder} />)}
            </div>
          )}
        </TabsContent>

        {/* CATEGORIES */}
        <TabsContent value="categories">
          <Panel
            title={`Menu-osiot (${categories.length})`}
            action={<Button size="sm" onClick={() => setCatDialog({ open: true, mode: "create", data: defaultCategory() })} className="btn-luxe inline-flex items-center gap-1" data-testid="cat-new">
              <Plus className="w-3.5 h-3.5" /> Uusi osio
            </Button>}
          >
            <p className="text-[11px] text-white/50 mb-3">Lisää uusia osioita kuten &apos;Shisha&apos;, &apos;Makeiset&apos;, &apos;Erikoispizzat&apos; tai mitä tahansa.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="categories-grid">
              {categories.map(c => {
                const Icon = ICON_MAP[c.icon] || Utensils;
                return (
                  <div key={c.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.04]" data-testid={`cat-${c.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${c.color}30` }}>
                          <Icon className="w-5 h-5" style={{ color: c.color }} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{c.name}</p>
                          <p className="text-[10px] text-white/40">{products.filter(p => p.category_id === c.id).length} tuotetta</p>
                        </div>
                      </div>
                      <div className="space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setCatDialog({ open: true, mode: "edit", data: c })}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => delCategory(c)} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                    {c.description && <p className="text-xs text-white/50 mt-2">{c.description}</p>}
                  </div>
                );
              })}
            </div>
          </Panel>
        </TabsContent>

        {/* PRODUCTS */}
        <TabsContent value="products">
          <Panel
            title={`Tuotteet (${products.length})`}
            action={<Button size="sm" onClick={() => setProdDialog({ open: true, mode: "create", data: defaultProduct() })} className="btn-luxe inline-flex items-center gap-1" data-testid="prod-new">
              <Plus className="w-3.5 h-3.5" /> Uusi tuote
            </Button>}
          >
            {categories.map(cat => {
              const catProducts = products.filter(p => p.category_id === cat.id);
              if (catProducts.length === 0) return null;
              const Icon = ICON_MAP[cat.icon] || Utensils;
              return (
                <div key={cat.id} className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" style={{ color: cat.color }} />
                    <h3 className="text-sm font-display text-white">{cat.name}</h3>
                    <span className="text-xs text-white/40">({catProducts.length})</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {catProducts.map(p => (
                      <div key={p.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-3 hover:bg-white/[0.04]" data-testid={`prod-${p.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{p.name}</p>
                            {p.description && <p className="text-[11px] text-white/40 line-clamp-2">{p.description}</p>}
                            <p className="text-base font-mono text-amber-300 mt-1">{formatEur(p.price)}</p>
                            {!p.is_available && <Badge className="bg-red-500/15 text-red-300 border-red-500/30 text-[10px] mt-1">Ei saatavilla</Badge>}
                          </div>
                          <div className="space-x-1 flex-shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => setProdDialog({ open: true, mode: "edit", data: p })}><Pencil className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => delProduct(p)} className="text-red-300"><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {products.length === 0 && (
              <EmptyState icon={<Pizza className="w-8 h-8" />} message="Ei tuotteita – luo ensimmäinen" action={<Button onClick={() => setProdDialog({ open: true, mode: "create", data: defaultProduct() })} className="btn-luxe">Lisää tuote</Button>} />
            )}
          </Panel>
        </TabsContent>

        {/* TABLES */}
        <TabsContent value="tables">
          <Panel
            title={`Pöydät (${tables.length})`}
            action={<Button size="sm" onClick={() => setTableDialog({ open: true, mode: "create", data: defaultTable() })} className="btn-luxe inline-flex items-center gap-1" data-testid="table-new">
              <Plus className="w-3.5 h-3.5" /> Uusi pöytä
            </Button>}
          >
            {tables.length === 0 ? (
              <EmptyState icon={<Users className="w-8 h-8" />} message="Lisää pöydät ensin – sitten voit tulostaa QR-koodit" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="tables-grid">
                {tables.map(t => (
                  <div key={t.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.04]" data-testid={`table-${t.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-2xl font-display text-white">Pöytä {t.table_number}</p>
                        <p className="text-[11px] text-white/40">{t.capacity} hlöä · {t.location || "—"}</p>
                      </div>
                      <div className="space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setTableDialog({ open: true, mode: "edit", data: t })}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => delTable(t)} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setQrPreview(t)} className="border-amber-400/30 inline-flex items-center gap-1 text-amber-200" data-testid={`table-qr-${t.id}`}>
                        <QrCode className="w-3.5 h-3.5" /> Näytä QR
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => regenQr(t)} className="text-white/60" title="Uudista QR-koodi">
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openStaffOrder(t.id)} className="text-emerald-300" title="Tee tilaus tähän pöytään">
                        <ChefHat className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>

        {/* ALL ORDERS */}
        <TabsContent value="all-orders">
          <Panel title={`Kaikki tilaukset (${orders.length})`}>
            {orders.length === 0 ? (
              <EmptyState icon={<ListChecks className="w-8 h-8" />} message="Ei tilauksia vielä" />
            ) : (
              <table className="w-full text-sm" data-testid="all-orders-table">
                <thead className="text-[10px] uppercase tracking-widest text-white/40">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-2">Nro</th>
                    <th className="text-left py-2 px-2">Pöytä</th>
                    <th className="text-left py-2 px-2">Lähde</th>
                    <th className="text-left py-2 px-2">Tuotteita</th>
                    <th className="text-right py-2 px-2">Summa</th>
                    <th className="text-center py-2 px-2">Tila</th>
                    <th className="text-left py-2 px-2">Aika</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {orders.map(o => {
                    const st = ORDER_STATUSES[o.status] || ORDER_STATUSES.received;
                    const sr = SOURCE_LABEL[o.source] || SOURCE_LABEL.staff;
                    return (
                      <tr key={o.id} className="hover:bg-white/[0.02]" data-testid={`order-row-${o.id}`}>
                        <td className="py-2 px-2 font-mono text-amber-300">{o.order_number}</td>
                        <td className="py-2 px-2 text-white">{o.table_number || "—"}</td>
                        <td className={`py-2 px-2 text-xs ${sr.color}`}>{sr.label}</td>
                        <td className="py-2 px-2 text-white/70 text-xs">{(o.items || []).map(i => `${i.quantity}× ${i.name}`).join(", ")}</td>
                        <td className="py-2 px-2 text-right font-mono text-emerald-300">{formatEur(o.total)}</td>
                        <td className="py-2 px-2 text-center"><Badge className={`${st.cls} border text-[10px]`}>{st.label}</Badge></td>
                        <td className="py-2 px-2 text-xs text-white/40">{o.created_at?.slice(11, 16)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Panel>
        </TabsContent>

        {/* === STAFF / HENKILÖKUNTA === */}
        <TabsContent value="staff">
          <RestaurantStaffManager />
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={catDialog.open} onOpenChange={v => !v && setCatDialog({ open: false, mode: "create", data: defaultCategory() })}>
        <DialogContent className="max-w-md" data-testid="cat-dialog">
          <DialogHeader><DialogTitle>{catDialog.mode === "create" ? "Uusi menu-osio" : "Muokkaa osiota"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Nimi *"><Input value={catDialog.data.name} onChange={e => setCatDialog({ ...catDialog, data: { ...catDialog.data, name: e.target.value } })} placeholder="esim. Shisha" data-testid="cat-name" /></Field>
            <Field label="Kuvaus"><Input value={catDialog.data.description || ""} onChange={e => setCatDialog({ ...catDialog, data: { ...catDialog.data, description: e.target.value } })} /></Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Kuvake">
                <select value={catDialog.data.icon} onChange={e => setCatDialog({ ...catDialog, data: { ...catDialog.data, icon: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {Object.keys(ICON_MAP).map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </Field>
              <Field label="Väri"><Input type="color" value={catDialog.data.color} onChange={e => setCatDialog({ ...catDialog, data: { ...catDialog.data, color: e.target.value } })} className="h-10" /></Field>
              <Field label="Järjestys"><Input type="number" value={catDialog.data.sort_order} onChange={e => setCatDialog({ ...catDialog, data: { ...catDialog.data, sort_order: parseInt(e.target.value) || 0 } })} /></Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatDialog({ open: false, mode: "create", data: defaultCategory() })}>Peruuta</Button>
            <Button onClick={saveCategory} disabled={saving} className="btn-luxe" data-testid="cat-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={prodDialog.open} onOpenChange={v => !v && setProdDialog({ open: false, mode: "create", data: defaultProduct() })}>
        <DialogContent
          className="max-w-2xl border-amber-500/20 bg-gradient-to-br from-zinc-900/95 via-zinc-950/95 to-zinc-900/95 backdrop-blur-2xl shadow-2xl shadow-amber-500/10"
          data-testid="prod-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-amber-200 flex items-center gap-2">
              <Utensils className="w-4 h-4" />
              {prodDialog.mode === "create" ? "Uusi tuote" : "Muokkaa tuotetta"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {/* VASEN: 3D-esikatselukortti */}
            <ProductPreviewCard
              data={prodDialog.data}
              categories={categories}
              onImageChange={(image_url) => setProdDialog({ ...prodDialog, data: { ...prodDialog.data, image_url } })}
            />

            {/* OIKEA: lomake */}
            <div className="space-y-3">
              <Field label="Nimi *">
                <Input value={prodDialog.data.name} onChange={e => setProdDialog({ ...prodDialog, data: { ...prodDialog.data, name: e.target.value } })} placeholder="esim. Margherita-pizza" data-testid="prod-name" />
              </Field>
              <Field label="Menu-osio *">
                <select value={prodDialog.data.category_id} onChange={e => setProdDialog({ ...prodDialog, data: { ...prodDialog.data, category_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="prod-category">
                  <option value="">— valitse —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Kuvaus">
                <textarea value={prodDialog.data.description || ""} onChange={e => setProdDialog({ ...prodDialog, data: { ...prodDialog.data, description: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]" placeholder="Kerro tuotteesta..." />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Hinta (€) *">
                  <Input type="number" step="0.01" value={prodDialog.data.price} onChange={e => setProdDialog({ ...prodDialog, data: { ...prodDialog.data, price: parseFloat(e.target.value) || 0 } })} data-testid="prod-price" />
                </Field>
                <Field label="ALV %">
                  <select value={prodDialog.data.vat_percent} onChange={e => setProdDialog({ ...prodDialog, data: { ...prodDialog.data, vat_percent: parseFloat(e.target.value) } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                    <option value="14">14 % (ruoka)</option>
                    <option value="25.5">25.5 % (alkoholi)</option>
                    <option value="10">10 % (vesi)</option>
                    <option value="0">0 %</option>
                  </select>
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                <input type="checkbox" checked={prodDialog.data.is_available} onChange={e => setProdDialog({ ...prodDialog, data: { ...prodDialog.data, is_available: e.target.checked } })} />
                Saatavilla asiakkaille
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setProdDialog({ open: false, mode: "create", data: defaultProduct() })}>Peruuta</Button>
            <Button onClick={saveProduct} disabled={saving} className="bg-gradient-to-r from-amber-500 to-orange-600 text-zinc-900 hover:from-amber-400 hover:to-orange-500 font-medium" data-testid="prod-save">
              {saving ? "Tallennetaan…" : "Tallenna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table Dialog */}
      <Dialog open={tableDialog.open} onOpenChange={v => !v && setTableDialog({ open: false, mode: "create", data: defaultTable() })}>
        <DialogContent className="max-w-md" data-testid="table-dialog">
          <DialogHeader><DialogTitle>{tableDialog.mode === "create" ? "Uusi pöytä" : "Muokkaa pöytää"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Pöytänumero *"><Input value={tableDialog.data.table_number} onChange={e => setTableDialog({ ...tableDialog, data: { ...tableDialog.data, table_number: e.target.value } })} placeholder="esim. 1, T-12, Terassi 4" data-testid="table-num" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Paikkoja"><Input type="number" value={tableDialog.data.capacity} onChange={e => setTableDialog({ ...tableDialog, data: { ...tableDialog.data, capacity: parseInt(e.target.value) || 4 } })} /></Field>
              <Field label="Sijainti">
                <select value={tableDialog.data.location} onChange={e => setTableDialog({ ...tableDialog, data: { ...tableDialog.data, location: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="Sali">Sali</option>
                  <option value="Terassi">Terassi</option>
                  <option value="VIP">VIP</option>
                  <option value="Baari">Baari</option>
                </select>
              </Field>
            </div>
            <Field label="Muistiinpanot"><Input value={tableDialog.data.notes || ""} onChange={e => setTableDialog({ ...tableDialog, data: { ...tableDialog.data, notes: e.target.value } })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTableDialog({ open: false, mode: "create", data: defaultTable() })}>Peruuta</Button>
            <Button onClick={saveTable} disabled={saving} className="btn-luxe" data-testid="table-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Preview Dialog */}
      <Dialog open={!!qrPreview} onOpenChange={v => !v && setQrPreview(null)}>
        <DialogContent className="max-w-md" data-testid="qr-preview-dialog">
          <DialogHeader><DialogTitle>QR-koodi · Pöytä {qrPreview?.table_number}</DialogTitle></DialogHeader>
          {qrPreview && (
            <div className="space-y-4 text-center">
              <div className="bg-white p-6 rounded-2xl mx-auto w-fit shadow-2xl shadow-amber-500/20">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=4&data=${encodeURIComponent(qrUrl(qrPreview))}`}
                  alt="QR-koodi"
                  className="w-64 h-64"
                  data-testid="qr-image"
                />
              </div>
              <div className="text-xs text-white/60 bg-black/30 p-3 rounded-lg break-all" data-testid="qr-url">{qrUrl(qrPreview)}</div>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => downloadQr(qrPreview)} className="btn-luxe inline-flex items-center gap-2" data-testid="qr-download">
                  <Download className="w-4 h-4" /> Lataa PNG
                </Button>
                <Button variant="outline" onClick={() => window.open(qrUrl(qrPreview), "_blank")} className="border-amber-400/30 inline-flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Avaa
                </Button>
              </div>
              <p className="text-[11px] text-white/40">Tulosta tämä QR-koodi ja kiinnitä pöytään. Asiakas skannaa puhelimellaan ja näkee menun.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Staff Order Dialog */}
      <Dialog open={orderDialog.open} onOpenChange={v => !v && setOrderDialog({ open: false, data: { table_id: "", customer_name: "", items: [], comment: "" } })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="staff-order-dialog">
          <DialogHeader><DialogTitle>Uusi tilaus (henkilökunnan näkymä)</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden flex-1">
            {/* Left: select category + products */}
            <div className="md:col-span-2 overflow-y-auto pr-2 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Pöytä">
                  <select value={orderDialog.data.table_id} onChange={e => setOrderDialog({ ...orderDialog, data: { ...orderDialog.data, table_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="order-table">
                    <option value="">— tiski / take-away —</option>
                    {tables.map(t => <option key={t.id} value={t.id}>Pöytä {t.table_number} ({t.location})</option>)}
                  </select>
                </Field>
                <Field label="Asiakkaan nimi (vapaaehtoinen)">
                  <Input value={orderDialog.data.customer_name} onChange={e => setOrderDialog({ ...orderDialog, data: { ...orderDialog.data, customer_name: e.target.value } })} placeholder="esim. Akiel" />
                </Field>
              </div>

              {categories.map(cat => {
                const prods = products.filter(p => p.category_id === cat.id && p.is_available);
                if (prods.length === 0) return null;
                const Icon = ICON_MAP[cat.icon] || Utensils;
                return (
                  <div key={cat.id}>
                    <p className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-1 mb-1">
                      <Icon className="w-3 h-3" style={{ color: cat.color }} /> {cat.name}
                    </p>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                      {prods.map(p => (
                        <button
                          key={p.id}
                          onClick={() => addItem(p)}
                          className="text-left rounded-lg border border-white/10 bg-white/[0.02] hover:bg-amber-500/10 hover:border-amber-400/30 p-2 transition"
                          data-testid={`menu-prod-${p.id}`}
                        >
                          <p className="text-sm text-white truncate">{p.name}</p>
                          <p className="text-xs text-amber-300 font-mono">{formatEur(p.price)}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {products.length === 0 && <p className="text-xs text-white/40 italic text-center py-8">Lisää tuotteita ensin menusta</p>}
            </div>

            {/* Right: cart */}
            <div className="border-l border-white/5 pl-3 flex flex-col" data-testid="order-cart">
              <p className="text-xs uppercase tracking-widest text-amber-200/70 mb-2">Tilauksen sisältö ({orderDialog.data.items.length})</p>
              <div className="flex-1 overflow-y-auto space-y-1 mb-3">
                {orderDialog.data.items.length === 0 ? (
                  <p className="text-xs text-white/30 italic text-center py-4">Klikkaa tuotteita lisätäksesi</p>
                ) : (
                  orderDialog.data.items.map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2 p-2 rounded bg-white/[0.03] text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="text-white truncate">{it.quantity}× {it.name}</p>
                        <p className="text-[10px] text-amber-300 font-mono">{formatEur(it.quantity * it.price)}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-red-300"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))
                )}
              </div>
              <Field label="Kommentit (esim. ei sipulia)">
                <textarea
                  value={orderDialog.data.comment || ""}
                  onChange={e => setOrderDialog({ ...orderDialog, data: { ...orderDialog.data, comment: e.target.value } })}
                  className="w-full bg-input/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs min-h-[50px]"
                  data-testid="order-comment"
                />
              </Field>
              <div className="border-t border-white/5 mt-3 pt-3 space-y-2">
                <div className="flex justify-between text-base">
                  <span className="text-white/70">Yhteensä</span>
                  <span className="font-mono text-emerald-300 font-semibold" data-testid="order-total">{formatEur(orderTotal)}</span>
                </div>
                <Button onClick={submitStaffOrder} disabled={saving || orderDialog.data.items.length === 0} className="btn-luxe w-full inline-flex items-center justify-center gap-2" data-testid="order-submit">
                  {saving ? "Lähetetään…" : "Lähetä keittiöön"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ====================== OrderCard ======================
function OrderCard({ order, onUpdate, onCancel }) {
  const st = ORDER_STATUSES[order.status] || ORDER_STATUSES.received;
  const sr = SOURCE_LABEL[order.source] || SOURCE_LABEL.staff;
  const flowIdx = ORDER_FLOW.indexOf(order.status);
  const isActive = flowIdx >= 0 && order.status !== "cancelled";
  const NextIcon = st.nextIcon || CheckCircle2;
  const itemsCount = (order.items || []).reduce((sum, it) => sum + (it.quantity || 0), 0);

  return (
    <div
      className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur p-4 hover:from-white/[0.05] hover:to-white/[0.02] transition relative overflow-hidden"
      data-testid={`order-card-${order.id}`}
    >
      {/* Vasen reuna värilippu */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${st.dot}`}></div>

      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-xs text-amber-300 font-mono">{order.order_number}</p>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${st.dot} animate-pulse`}></span>
            <span className="text-[10px] text-white/40">{st.short}</span>
          </div>
          <p className="text-lg font-display text-white">
            Pöytä {order.table_number || "—"}
            <span className="text-[10px] text-white/40 ml-2 font-sans">· {itemsCount} tuote{itemsCount === 1 ? "" : "tta"}</span>
          </p>
          <p className={`text-[10px] uppercase tracking-widest mt-0.5 ${sr.color}`}>
            {sr.label}{order.customer_name && ` · ${order.customer_name}`}
          </p>
        </div>
        <Badge className={`${st.cls} border text-[10px] whitespace-nowrap`}>{st.label}</Badge>
      </div>

      {/* 5-vaiheinen aikajana */}
      {isActive && (
        <div className="flex items-center gap-1 mb-3" data-testid={`order-timeline-${order.id}`}>
          {ORDER_FLOW.map((step, idx) => {
            const done = idx <= flowIdx;
            const current = idx === flowIdx;
            const meta = ORDER_STATUSES[step];
            return (
              <div key={step} className="flex-1 flex items-center" title={meta.label}>
                <div
                  className={`flex-1 h-1.5 rounded-full transition-all ${
                    done ? meta.dot : "bg-white/5"
                  } ${current ? "ring-2 ring-white/30 ring-offset-1 ring-offset-zinc-900" : ""}`}
                ></div>
              </div>
            );
          })}
        </div>
      )}

      <ul className="space-y-0.5 text-sm border-t border-white/5 pt-2 mb-2">
        {(order.items || []).map((it, idx) => (
          <li key={idx} className="flex justify-between">
            <span className="text-white/80">{it.quantity}× {it.name}</span>
            <span className="text-white/40 font-mono text-xs">{formatEur(it.quantity * it.price)}</span>
          </li>
        ))}
      </ul>
      {order.comment && (
        <p className="text-[11px] text-amber-200 bg-amber-500/10 rounded p-2 mb-2 italic">💬 {order.comment}</p>
      )}
      <div className="flex items-center justify-between text-sm border-t border-white/5 pt-2 mb-3">
        <span className="text-white/40 text-xs">{order.created_at?.slice(11, 16)}</span>
        <span className="text-emerald-300 font-mono font-semibold text-base">{formatEur(order.total)}</span>
      </div>

      {/* ISO seuraava-vaihe painike (Phase 20) */}
      <div className="flex gap-2">
        {st.next ? (
          <Button
            onClick={() => onUpdate(order, st.next)}
            className={`flex-1 inline-flex items-center justify-center gap-2 font-semibold py-2.5 ${
              order.status === "received"   ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-400 hover:to-blue-500" :
              order.status === "preparing"  ? "bg-gradient-to-r from-amber-500 to-orange-500 text-zinc-900 hover:from-amber-400 hover:to-orange-400" :
              order.status === "ready"      ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-zinc-900 hover:from-emerald-400 hover:to-teal-400" :
              "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-400 hover:to-pink-400"
            }`}
            data-testid={`order-next-${order.id}`}
          >
            <NextIcon className="w-4 h-4" /> {st.nextLabel}
          </Button>
        ) : (
          <div className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-md bg-white/[0.02] text-white/40 text-xs">
            {order.status === "paid" ? <><CheckCircle2 className="w-4 h-4 text-green-400" /> Tilaus valmis</> : <>Tilaus peruttu</>}
          </div>
        )}
        {st.next && (
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onCancel(order)}
            className="text-red-300 hover:bg-red-500/10 h-auto"
            data-testid={`order-cancel-${order.id}`}
            title="Peruuta tilaus"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-white/60">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ============================================================================
// ProductPreviewCard – 3D-lasinen esikatselu + kuvan lisäys (kamera/tiedosto)
// Phase 20 – Ravintolan tuotteet
// ============================================================================
function ProductPreviewCard({ data, categories, onImageChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const cameraRef = React.useRef(null);
  const category = categories.find(c => c.id === data.category_id);

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Kuva on liian suuri (max 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const b64 = ev.target.result;
          const r = await api.post("/restaurant/upload-image", {
            image_base64: b64,
            mime: file.type || "image/jpeg",
          });
          // Käytä backendin storage-URL:a (suora servointi)
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/restaurant/image/${r.data.path}`;
          onImageChange(url);
          toast.success("Kuva ladattu");
        } catch (err) {
          toast.error(formatApiError(err));
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error("Kuvan lataus epäonnistui");
      setUploading(false);
    }
  };

  const previewName = data.name || "Tuotteen nimi";
  const previewPrice = (parseFloat(data.price) || 0).toFixed(2);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-white/60">Esikatselu (asiakkaan näkymä)</Label>
      <div
        className="relative rounded-2xl overflow-hidden border border-white/10 backdrop-blur-xl shadow-2xl transition-transform hover:scale-[1.02] hover:-rotate-1"
        style={{
          background: "linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(168,85,247,0.06) 50%, rgba(99,102,241,0.04) 100%)",
          boxShadow: "0 25px 50px -12px rgba(251,191,36,0.15), inset 0 1px 0 rgba(255,255,255,0.1)",
        }}
        data-testid="prod-preview-card"
      >
        {/* Kuva-alue */}
        <div className="relative h-44 bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 overflow-hidden">
          {data.image_url ? (
            <>
              <img
                src={data.image_url}
                alt={previewName}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
                data-testid="prod-preview-image"
              />
              <button
                type="button"
                onClick={() => onImageChange("")}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-red-500/80 backdrop-blur flex items-center justify-center transition"
                data-testid="prod-preview-image-remove"
                title="Poista kuva"
              >
                <Trash2 className="w-3.5 h-3.5 text-white" />
              </button>
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/30">
              <Utensils className="w-10 h-10 mb-2" />
              <p className="text-[10px] uppercase tracking-widest">Ei kuvaa vielä</p>
            </div>
          )}
          {/* Hinta-badge */}
          <div className="absolute bottom-2 right-2 bg-gradient-to-r from-amber-400 to-orange-500 text-zinc-900 px-3 py-1 rounded-full text-sm font-bold shadow-lg">
            {previewPrice} €
          </div>
        </div>

        {/* Tekstiosa */}
        <div className="p-3 bg-zinc-950/60">
          {category && (
            <p className="text-[9px] uppercase tracking-widest text-amber-300/80 mb-1">{category.name}</p>
          )}
          <p className="text-white font-medium text-sm truncate">{previewName}</p>
          {data.description && (
            <p className="text-[11px] text-white/50 line-clamp-2 mt-1">{data.description}</p>
          )}
          {!data.is_available && (
            <Badge className="bg-red-500/15 text-red-300 text-[10px] mt-2">Loppunut</Badge>
          )}
        </div>
      </div>

      {/* Kuvan lisäyspainikkeet */}
      <div className="grid grid-cols-2 gap-1.5">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="prod-image-file-input"
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="prod-image-camera-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => cameraRef.current?.click()}
          className="border-amber-400/20 text-amber-200 hover:bg-amber-500/10"
          data-testid="prod-image-camera-button"
        >
          {uploading ? <Loader2Icon /> : <CameraIcon />} <span className="ml-1">Kamera</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="border-cyan-400/20 text-cyan-200 hover:bg-cyan-500/10"
          data-testid="prod-image-upload-button"
        >
          {uploading ? <Loader2Icon /> : <UploadIcon />} <span className="ml-1">Lataa</span>
        </Button>
      </div>
      <p className="text-[10px] text-white/30 text-center">
        Kamera (📷) ottaa kuvan puhelimella · Lataa (📂) tuo kuvan laitteelta
      </p>
    </div>
  );
}

// Pikkukuvakkeet (tämän tiedoston sisäisesti, jotta vältetään uusia tuonteja)
function CameraIcon() { return <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>; }
function UploadIcon() { return <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>; }
function Loader2Icon() { return <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>; }

